package com.xorrus.trading.lot.relayclient.model

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlin.time.Instant

/**
 * 0DTE Opening Put IWM — Remote-Typisierung analog zum SPY-Pendant ([OpeningPutParams]).
 *
 * Anders als das SPY-Pendant (nackter Short Put) ist die IWM-Variante ein Put Credit
 * Spread mit ZWEI Beinen (Short ≈ −0,5 Delta, Long 5 Punkte tiefer) — die bestätigten
 * Kontrakte reisen deshalb als Liste ([OpeningPutIWMParams.expectedContracts],
 * [OpeningPutIWMPreview.contracts]). Underlying (IWM), Zeitfenster, Einstiegsfilter
 * (Wochentag Mo/Di/Fr, Gap, ROC3) und SL sind fixe Strategie-Konstanten; die
 * Remote-Params spiegeln exakt das, was auch die Desktop-View anbietet.
 */

val OPENING_PUT_IWM_STRATEGY_ID = StrategyId("openingPutIWM")
const val OPENING_PUT_IWM_SCHEMA_VERSION = 1

@Serializable
@SerialName("openingPutIWM")
data class OpeningPutIWMParams(
    /** Konto (dynamische Auswahl, [OpeningPutIWMOptions]) */
    val accountId: AccountId,
    /** An Börse übertragen */
    val transmitToExchange: Boolean,
    /** Anzahl 1..1000 (Grenze der Strategie) */
    val quantity: Int,
    /**
     * PreCheck (Wochentag/Zeitfenster, Gap, ROC3) übergehen — desktopseitig zusätzlich
     * an die Lizenz FEATURE_IGNORE_PRECONDITION gebunden (wie in der Desktop-View).
     * Teaminterne Funktion, nicht für Kunden.
     */
    val ignorePreconditions: Boolean = false,
    /**
     * erst bis 10:25 NY warten (Pendant zum „zeitgesteuert starten"-Button der View) —
     * desktopseitig an FEATURE_TIME_BASED_TRADING gebunden. Teaminterne Funktion,
     * nicht für Kunden; platziert ohne erneute Kontrakt-Bestätigung.
     */
    val waitForWindow: Boolean = false,
    /**
     * Vom Nutzer bestätigte Spread-Beine in Symbol-Notation (Short Put, Long Put), aus
     * `strategy.preview` ([OpeningPutIWMPreview.contracts]). PFLICHT für den Sofort-Start
     * (waitForWindow = false): Der Desktop ermittelt die Beine beim Platzieren erneut aus
     * Live-Preisen und platziert nur bei Übereinstimmung (Compliance). Bei
     * waitForWindow = true ignoriert. Nicht in Defaults/Presets persistieren — die
     * Bestätigung gilt nur für den einen Start.
     */
    val expectedContracts: List<String>? = null,
    override val schemaVersion: Int = OPENING_PUT_IWM_SCHEMA_VERSION,
) : StrategyParams

@Serializable
@SerialName("openingPutIWM")
data class OpeningPutIWMOptions(
    val accounts: List<AccountRef>,
    override val asOf: Instant,
    /** Lizenz-Freigaben der teaminternen Schalter ([ChecklistGates]); Default gesperrt. */
    override val ignorePreconditionsAllowed: Boolean = false,
    override val waitForWindowAllowed: Boolean = false,
) : StrategyOptions, ChecklistGates

/**
 * Ergebnis von `strategy.preview` (Compliance-Vorschau für den Sofort-Start): die aus
 * Live-Preisen ermittelten Spread-Beine plus alle Orders, die platziert würden. Der
 * Remote-Nutzer bestätigt genau diese Beine; die Bestätigung wandert als
 * [OpeningPutIWMParams.expectedContracts] in `strategy.activate`, wo der Desktop beim
 * Platzieren erneut ermittelt und nur bei Übereinstimmung platziert. KEIN Cache —
 * jede Vorschau rechnet frisch.
 */
@Serializable
data class OpeningPutIWMPreview(
    /** Symbol-Notation der ermittelten Beine (Short Put, Long Put); leer = Ermittlung fehlgeschlagen (siehe [message]). */
    val contracts: List<String> = emptyList(),
    /** Anzeigefertige Zeile pro Order (Entry, Stop Loss, Time-Exit). */
    val orders: List<String> = emptyList(),
    /** Fehlermeldung, wenn keine Kontrakte ermittelt werden konnten. */
    val message: String? = null,
    /** Zweisprachige Fassung von [message]; null bei alten Desktops. */
    val messageText: RemoteText? = null,
    val asOf: Instant,
)

@Serializable
@SerialName("openingPutIWM")
data class OpeningPutIWMStatus(
    override val phase: StrategyPhase,
    override val activeParams: StrategyParams? = null,
    override val since: Instant? = null,
    override val message: String? = null,
    override val messageText: RemoteText? = null,
    val steps: List<RemoteChecklistStep> = emptyList(),
    /** PreCheck wurde für diesen Lauf übersprungen */
    val preconditionsIgnored: Boolean = false,
) : RemoteStrategyStatus {
    override val strategyId: StrategyId get() = OPENING_PUT_IWM_STRATEGY_ID
}

// ---- Bounds -------------------------------------------------------------------------

data class OpeningPutIWMBounds(
    val maxQuantity: Int? = 10,
    val allowTransmitToExchange: Boolean = false,
) : ParamsBounds<OpeningPutIWMParams> {

    override fun violations(params: OpeningPutIWMParams): List<String> = violationsText(params).map { it.de }

    override fun violationsText(params: OpeningPutIWMParams): List<RemoteText> {
        val violations = mutableListOf<RemoteText>()
        if (maxQuantity != null && params.quantity > maxQuantity) {
            violations += RemoteText(
                de = "quantity ${params.quantity} überschreitet das Limit $maxQuantity",
                en = "quantity ${params.quantity} exceeds the limit $maxQuantity",
            )
        }
        if (params.transmitToExchange && !allowTransmitToExchange) {
            violations += RemoteText(
                de = "transmitToExchange ist für Remote-Aktivierungen nicht freigegeben",
                en = "transmitToExchange is not enabled for remote activations",
            )
        }
        return violations
    }
}

// ---- Geteilte Validierung (§3.2) ------------------------------------------------------

fun validateOpeningPutIWM(params: OpeningPutIWMParams, options: OpeningPutIWMOptions?): List<ValidationError> {
    val errors = mutableListOf<ValidationError>()

    if (params.schemaVersion != OPENING_PUT_IWM_SCHEMA_VERSION) {
        errors += ValidationError(
            field = "schemaVersion",
            message = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $OPENING_PUT_IWM_SCHEMA_VERSION) — Controller-App aktualisieren",
            code = ValidationCode.UNSUPPORTED_SCHEMA,
            messageText = RemoteText(
                de = "Unbekannte schemaVersion ${params.schemaVersion} (unterstützt: $OPENING_PUT_IWM_SCHEMA_VERSION) — Controller-App aktualisieren",
                en = "Unknown schemaVersion ${params.schemaVersion} (supported: $OPENING_PUT_IWM_SCHEMA_VERSION) — update the controller app",
            ),
        )
    }
    if (params.quantity !in 1..1000) {
        errors += ValidationError(
            "quantity",
            "Anzahl muss zwischen 1 und 1000 liegen",
            messageText = RemoteText(
                de = "Anzahl muss zwischen 1 und 1000 liegen",
                en = "Quantity must be between 1 and 1000",
            ),
        )
    }
    if (options != null && options.accounts.none { it.id == params.accountId }) {
        errors += ValidationError(
            field = "accountId",
            message = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
            code = ValidationCode.STALE_OPTION,
            messageText = RemoteText(
                de = "Das gewählte Konto ist nicht (mehr) verfügbar — Auswahl neu laden",
                en = "The selected account is not (or no longer) available — reload the selection",
            ),
        )
    }
    errors += checklistGateErrors(params.ignorePreconditions, params.waitForWindow, options)
    return errors
}
